import { slotId } from './text-block.js';
import { SPREAD_LAYOUTS, nearestTextPosition, textCapacityFor } from './layouts.js';
import { measureFit, wrapText } from './text-metrics.js';
/**
 * Доля вместимости слота, которую занимает комфортно свёрстанный блок.
 *
 * Калибровка: медиана 214 символов на блок в референсной вёрстке.
 * После увеличения поля до 10 мм геометрический предел при 15 pt — 43 × 7.
 * Коэффициент используется только для
 * ориентиров в брифе генератору сюжета — это не жёсткая отсечка.
 *
 * ⚠️ Измерением подтверждён **только** `full-bleed`: 214 символов на блок,
 * 428 на разворот — это медиана по 26 блокам реально свёрстанной книги.
 * Значения для `split` (≈536) и `band` (≈185 при ratio 0.7) — экстраполяция
 * той же геометрии на раскладки, которых в свёрстанном виде не существует.
 * Не принимать их за измерение; перекалибровать на первых реальных книгах
 * (01-SPEC.md §3.10.6).
 */
export const COMFORTABLE_FILL_RATIO = 0.57;
export function textBudgetFor(layoutId, attributes, sizePt = attributes.typography.baseSizePt) {
    const capacity = textCapacityFor(layoutId, attributes, sizePt);
    const textSlots = SPREAD_LAYOUTS[layoutId].textSlots;
    const perBlock = Math.round(capacity.maxCharsPerLine * capacity.maxLines * COMFORTABLE_FILL_RATIO);
    return { capacity, perBlock, perSpread: perBlock * textSlots, textSlots };
}
/** Помещается ли текст в слот раскладки при заданном кегле. */
export function fitsInSlot(text, layoutId, attributes, sizePt = attributes.typography.baseSizePt) {
    return measureTextFit(text, layoutId, attributes, sizePt).fits;
}
/** Подробный замер: строки, вместимость и число лишних символов. */
export function measureTextFit(text, layoutId, attributes, sizePt = attributes.typography.baseSizePt) {
    return measureFit(text, textCapacityFor(layoutId, attributes, sizePt));
}
/**
 * Автоподбор кегля в коридоре `typography.autoFitPt` (01-SPEC.md §3.10.6).
 * Возвращает наибольший кегль коридора, при котором текст помещается,
 * или `null`, если не помещается и на нижней границе — тогда UI показывает
 * предупреждение с числом лишних символов. Текст не обрезается никогда.
 */
export function autoFitSizePt(text, layoutId, attributes) {
    const [minPt, maxPt] = attributes.typography.autoFitPt;
    const lo = Math.min(minPt, maxPt);
    const hi = Math.max(minPt, maxPt);
    const step = 0.5;
    for (let size = hi; size >= lo - 1e-9; size -= step) {
        const candidate = Math.round(size * 2) / 2;
        if (fitsInSlot(text, layoutId, attributes, candidate))
            return candidate;
    }
    return null;
}
/**
 * Равномерно раскладывает последовательные части по слотам, не меняя их порядок.
 * `separator` остаётся частью авторской структуры: пустая строка разделяет
 * текстовые блоки, одинарный перенос — строки внутри блока.
 */
function balanceChunks(parts, count, separator) {
    const chunks = Array.from({ length: count }, () => '');
    if (parts.length === 0)
        return chunks;
    if (count <= 1)
        return [parts.join(separator)];
    if (parts.length <= count) {
        parts.forEach((part, index) => {
            chunks[index] = part;
        });
        return chunks;
    }
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    let index = 0;
    let used = 0;
    parts.forEach((part, position) => {
        const partsLeft = parts.length - position;
        const chunksLeft = count - index;
        const target = (total * (index + 1)) / count;
        if (index < count - 1 && used >= target && partsLeft > chunksLeft - 1)
            index += 1;
        else if (index < count - 1 && partsLeft <= chunksLeft - 1)
            index += 1;
        const current = chunks[index] ?? '';
        chunks[index] = current.length > 0 ? `${current}${separator}${part}` : part;
        used += part.length;
    });
    return chunks;
}
/** Разбивает текст на слоты, сохраняя явно заданные автором переносы. */
function splitIntoChunks(text, count) {
    const normalized = text
        .replace(/\r\n?/gu, '\n')
        .split('\n')
        .map((line) => line.replace(/[^\S\n]+/gu, ' ').trim())
        .join('\n')
        .trim();
    const empty = Array.from({ length: count }, () => '');
    if (normalized.length === 0)
        return empty;
    if (count <= 1)
        return [normalized];
    // Пустая строка — явная граница текстовых блоков. Одинарные переносы
    // внутри каждого блока передаются дальше в wrapText как жёсткие.
    const paragraphs = normalized
        .split(/\n{2,}/u)
        .map((part) => part.trim())
        .filter(Boolean);
    if (paragraphs.length > 1)
        return balanceChunks(paragraphs, count, '\n\n');
    if (normalized.includes('\n')) {
        const explicit = empty.slice();
        explicit[0] = normalized;
        return explicit;
    }
    const matched = normalized.match(/[^.!?…]+(?:[.!?…]+["»']?)?/gu) ?? [normalized];
    const sentences = matched.map((s) => s.trim()).filter((s) => s.length > 0);
    return balanceChunks(sentences, count, ' ');
}
/**
 * `layoutText(textRu, layout, attributes)` — разбивка сплошного текста разворота
 * на блоки с переносами (01-SPEC.md §3.10.6).
 *
 * Переносы ставятся по смысловым границам, слова не режутся, висячих строк
 * короче трёх слов не остаётся.
 */
export function layoutText(textRu, layoutId, attributes, options = {}) {
    const layout = SPREAD_LAYOUTS[layoutId];
    const sizePt = options.sizePt ?? attributes.typography.baseSizePt;
    const capacity = textCapacityFor(layoutId, attributes, sizePt);
    const chunks = splitIntoChunks(textRu, layout.textSlots);
    const previous = options.previous ?? [];
    const blocks = [];
    for (let index = 0; index < layout.textSlots; index += 1) {
        const kept = previous[index];
        if (options.preserveManualText !== false && kept?.manual === true) {
            blocks.push({ ...kept, slotId: slotId(index) });
            continue;
        }
        const chunk = chunks[index] ?? '';
        const block = {
            slotId: slotId(index),
            text: chunk.length > 0 ? wrapText(chunk, capacity.maxCharsPerLine).join('\n') : '',
            position: nearestTextPosition(kept?.position, layoutId, index),
        };
        const textColor = kept?.textColor ?? attributes.typography.defaultTextColor;
        const highlightColor = kept?.highlightColor ?? attributes.typography.highlight;
        block.textColor = textColor;
        block.highlightColor = highlightColor;
        blocks.push(block);
    }
    return blocks;
}
//# sourceMappingURL=text-layout.js.map